import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel, Link, MenuItem, Select, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { numFont } from '../../utils/fonts';
import { PL_API, purchaseErrorMessage } from '../purchase-invoices/purchaseUtils';

/**
 * "Ostolaskutus" section of the vendor dialog (purchase ledger orgs, existing vendors only):
 * payment terms, default cost category, notes, and the vendor's known bank accounts.
 */
export default function VendorPurchaseSection({ vendorId, textFieldStyles }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [newIban, setNewIban] = useState('');
  const [message, setMessage] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const [profileResponse, categoriesResponse] = await Promise.all([
        axios.get(`${PL_API}/vendors/${vendorId}/profile/`),
        axios.get(`${PL_API}/cost-categories/`),
      ]);
      setProfile(profileResponse.data);
      setForm({
        payment_terms_days: profileResponse.data.payment_terms_days ?? '',
        default_cost_category: profileResponse.data.default_cost_category || '',
        notes: profileResponse.data.notes || '',
      });
      setCategories(Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []);
    } catch (error) {
      setProfile(null);
      setLoadFailed(true);
    }
  }, [vendorId]);

  useEffect(() => {
    if (vendorId) load();
  }, [vendorId, load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${PL_API}/vendors/${vendorId}/profile/`, {
        payment_terms_days: form.payment_terms_days === '' ? null : parseInt(form.payment_terms_days, 10),
        default_cost_category: form.default_cost_category || null,
        notes: form.notes,
      });
      setMessage({ severity: 'success', text: t('purchaseLedger.vendor.profileSaved') });
      load();
    } catch (error) {
      setMessage({ severity: 'error', text: purchaseErrorMessage(t, error) });
    } finally {
      setSaving(false);
    }
  };

  const addAccount = async () => {
    setBusy('add');
    try {
      await axios.post(`${PL_API}/vendors/${vendorId}/bank-accounts/`, { iban: newIban.replace(/\s+/g, '').toUpperCase() });
      setNewIban('');
      load();
    } catch (error) {
      setMessage({ severity: 'error', text: purchaseErrorMessage(t, error) });
    } finally {
      setBusy('');
    }
  };

  const verify = async (account) => {
    setBusy(`verify-${account.id}`);
    try {
      await axios.post(`${PL_API}/vendors/${vendorId}/bank-accounts/${account.id}/verify/`, {});
      load();
    } catch (error) {
      setMessage({ severity: 'error', text: purchaseErrorMessage(t, error) });
    } finally {
      setBusy('');
    }
  };

  const toggleActive = async (account) => {
    setBusy(`toggle-${account.id}`);
    try {
      await axios.patch(`${PL_API}/vendors/${vendorId}/bank-accounts/${account.id}/`, { is_active: !account.is_active });
      load();
    } catch (error) {
      setMessage({ severity: 'error', text: purchaseErrorMessage(t, error) });
    } finally {
      setBusy('');
    }
  };

  if (!form) {
    if (loadFailed) {
      return <Alert severity="warning" sx={{ mt: 2 }}>{t('purchaseLedger.vendor.loadFailed')}</Alert>;
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }} data-testid="vendor-purchase-section">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.secondary' }}>{t('purchaseLedger.vendor.sectionTitle')}</Typography>
        <Link component={RouterLink} to={`/ostolaskut?tab=all&vendor=${vendorId}`} variant="body2">
          {t('purchaseLedger.vendor.history')} ({profile?.invoice_count ?? 0})
        </Link>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            size="small"
            type="number"
            label={t('purchaseLedger.vendor.paymentTerms')}
            value={form.payment_terms_days}
            onChange={(e) => setForm((f) => ({ ...f, payment_terms_days: e.target.value }))}
            sx={{ ...textFieldStyles, flex: 1 }}
            inputProps={{ min: 0, max: 180 }}
          />
          <FormControl size="small" sx={{ ...textFieldStyles, flex: 2 }}>
            <InputLabel>{t('purchaseLedger.vendor.defaultCategory')}</InputLabel>
            <Select value={form.default_cost_category} label={t('purchaseLedger.vendor.defaultCategory')} onChange={(e) => setForm((f) => ({ ...f, default_cost_category: e.target.value }))}>
              <MenuItem value="">
                <em>{t('purchaseLedger.fields.noCategory')}</em>
              </MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <TextField size="small" multiline rows={2} label={t('purchaseLedger.vendor.apNotes')} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} sx={textFieldStyles} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" variant="outlined" onClick={save} disabled={saving} data-testid="vendor-purchase-save">
            {t('purchaseLedger.vendor.saveProfile')}
          </Button>
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('purchaseLedger.vendor.bankAccounts')}</Typography>
        {(profile?.bank_accounts || []).length === 0 && (
          <Typography variant="body2" color="text.secondary">{t('purchaseLedger.vendor.noBankAccounts')}</Typography>
        )}
        {(profile?.bank_accounts || []).map((account) => (
          <Box key={account.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} data-testid={`vendor-bank-account-${account.id}`}>
            <Typography sx={{ ...numFont, fontWeight: 600, opacity: account.is_active ? 1 : 0.5 }}>{account.iban}</Typography>
            {account.bic && <Typography variant="caption" color="text.secondary">{account.bic}</Typography>}
            {account.verified_at ? (
              <Chip size="small" color="success" label={t('purchaseLedger.vendor.verified')} />
            ) : (
              <Chip size="small" color="warning" variant="outlined" label={t('purchaseLedger.vendor.unverified')} />
            )}
            {!account.is_active && <Chip size="small" variant="outlined" label={t('purchaseLedger.vendor.inactive')} />}
            <Box sx={{ flex: 1 }} />
            {!account.verified_at && account.is_active && (
              <Button size="small" disabled={!!busy} onClick={() => verify(account)}>{t('purchaseLedger.vendor.verify')}</Button>
            )}
            <Button size="small" color="inherit" disabled={!!busy} onClick={() => toggleActive(account)}>
              {account.is_active ? t('purchaseLedger.vendor.deactivate') : t('purchaseLedger.vendor.activate')}
            </Button>
          </Box>
        ))}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField size="small" label={t('purchaseLedger.vendor.newIban')} value={newIban} onChange={(e) => setNewIban(e.target.value)} sx={{ ...textFieldStyles, flex: 1 }} />
          <Button size="small" variant="outlined" disabled={!newIban.trim() || !!busy} onClick={addAccount}>{t('Add')}</Button>
        </Box>
        {message && (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>
        )}
      </Box>
    </Box>
  );
}
